<script setup lang="ts">
  import { computed } from 'vue'
  import { storeToRefs } from 'pinia'
  import { useSelectedVisualizationColumnStore } from '@/stores/selectedVisualizationColumnStore'

  const props = defineProps({
    giPoint: {
      type: Object,
      required: true
    }
  })
  const boreholeSampleObservationStore = useBoreholeSampleObservationStore()
  const selectedVisualizationColumnStore = useSelectedVisualizationColumnStore()
  const giPointStore = useGIPointsStore()
  const { rawSelectedColumns } = storeToRefs(selectedVisualizationColumnStore)
  const tableItems = computed(() =>
    boreholeSampleObservationStore.shownDataByGIPointId(props.giPoint.id)
  )
  const sampleCountMapping = computed(() =>
    tableItems.value.reduce((mapping, item) => {
      for (const key of Object.keys(item)) {
        mapping[key] = (mapping[key] ?? 0) + 1
      }
      return mapping
    }, {})
  )
  const tableHeaders = computed(() => {
    const result = [{ title: 'Sample ID', key: 'sample_id' }]
    for (const column of rawSelectedColumns.value) {
      result.push({ title: column, key: column })
    }
    return result
  })

  function onGIPointNameClicked() {
    giPointStore.setCurrentItem(props.giPoint)
  }

  function calculateLabRowHeight(numberOfItems: number) {
    if (numberOfItems < 1) {
      return 100
    }
    const header = 50
    const base = 60
    const maxCount = 10
    const maxHeight = header + maxCount * base
    return Math.min(maxHeight, header + numberOfItems * base)
  }
</script>

<template>
  <v-list-group subgroup>
    <template v-slot:activator="{ props: activatorProps }">
      <v-list-item v-bind="activatorProps">
        <v-list-item-title class="text-primary">
          <div>
            <span @click.stop="onGIPointNameClicked">
              {{ props.giPoint.properties.location_id }}
            </span>
          </div>
        </v-list-item-title>
      </v-list-item>
    </template>
    <v-data-table-virtual
      :headers="tableHeaders"
      :items="tableItems"
      density="compact"
      :hover="true"
      no-data-text="No Lab Results available."
      :fixed-header="true"
      :height="calculateLabRowHeight(tableItems.length)"
    >
      <template v-for="header in tableHeaders" :key="header.key" v-slot:[`header.${header.key}`]>
        <div class="d-flex justify-start align-center">
          <p>{{ header.title }}</p>
          <v-chip class="ml-2" color="primary" variant="tonal" size="x-small">
            {{ sampleCountMapping[header.key] ?? 0 }}
          </v-chip>
        </div>
      </template>
    </v-data-table-virtual>
  </v-list-group>
</template>

<style scoped>
  :deep(.v-data-table-footer) {
    display: none;
  }
</style>
